import { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import {
  Type, Image, Square, Minus, MoveVertical, GripVertical,
  ChevronUp, ChevronDown, Trash2, Plus, Eye, Code, Link,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic,
} from 'lucide-react';

const BLOCK_TYPES = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'button', label: 'Button', icon: Square },
  { type: 'divider', label: 'Divider', icon: Minus },
  { type: 'spacer', label: 'Spacer', icon: MoveVertical },
];

const MERGE_TAGS = [
  { tag: '{{customer_first_name}}', label: 'First Name' },
  { tag: '{{customer_last_name}}', label: 'Last Name' },
  { tag: '{{customer_name}}', label: 'Full Name' },
  { tag: '{{company_name}}', label: 'Company' },
  { tag: '{{company_phone}}', label: 'Phone' },
  { tag: '{{estimate_link}}', label: 'Estimate Link' },
  { tag: '{{invoice_link}}', label: 'Invoice Link' },
  { tag: '{{unsubscribe_url}}', label: 'Unsubscribe' },
];

const DEFAULT_BLOCKS = {
  text: { type: 'text', content: '<p>Write your content here...</p>', align: 'left' },
  image: { type: 'image', src: '', alt: 'Image', width: '100%', align: 'center' },
  button: { type: 'button', text: 'Click Here', url: '', bgColor: '#2563eb', textColor: '#ffffff', align: 'center', borderRadius: '6' },
  divider: { type: 'divider', color: '#e5e7eb', thickness: '1' },
  spacer: { type: 'spacer', height: '20' },
};

export function BlockEmailEditor({ blocks, onChange, onHtmlChange }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const addBlock = (type) => {
    const newBlock = { ...DEFAULT_BLOCKS[type], id: Date.now().toString() };
    const updated = [...blocks, newBlock];
    onChange(updated);
    setSelectedIdx(updated.length - 1);
  };

  const updateBlock = (idx, updates) => {
    const updated = blocks.map((b, i) => i === idx ? { ...b, ...updates } : b);
    onChange(updated);
  };

  const removeBlock = (idx) => {
    const updated = blocks.filter((_, i) => i !== idx);
    onChange(updated);
    setSelectedIdx(null);
  };

  const moveBlock = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const updated = [...blocks];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    onChange(updated);
    setSelectedIdx(target);
  };

  const html = blocksToHtml(blocks);

  // Sync HTML to parent whenever blocks change
  const prevHtmlRef = { current: '' };
  if (html !== prevHtmlRef.current) {
    prevHtmlRef.current = html;
    if (onHtmlChange) onHtmlChange(html);
  }

  return (
    <div className="space-y-3" data-testid="block-email-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-medium text-muted-foreground uppercase mr-1">Add Block:</span>
        {BLOCK_TYPES.map(bt => {
          const Icon = bt.icon;
          return (
            <Button
              key={bt.type}
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => addBlock(bt.type)}
              data-testid={`add-block-${bt.type}`}
            >
              <Icon className="w-3 h-3" /> {bt.label}
            </Button>
          );
        })}
        <div className="flex-1" />
        <Button
          variant={showPreview ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => setShowPreview(!showPreview)}
          data-testid="toggle-preview"
        >
          {showPreview ? <Code className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {showPreview ? 'Editor' : 'Preview'}
        </Button>
      </div>

      {showPreview ? (
        <div className="border rounded-lg p-4 bg-gray-50 min-h-[200px]">
          <div className="max-w-[600px] mx-auto bg-white shadow-sm rounded-lg overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        </div>
      ) : (
        <>
          {blocks.length === 0 ? (
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Type className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">Add blocks to build your email</p>
            </div>
          ) : (
            <div className="space-y-1">
              {blocks.map((block, idx) => (
                <BlockItem
                  key={block.id || idx}
                  block={block}
                  index={idx}
                  isSelected={selectedIdx === idx}
                  isFirst={idx === 0}
                  isLast={idx === blocks.length - 1}
                  onSelect={() => setSelectedIdx(selectedIdx === idx ? null : idx)}
                  onUpdate={(updates) => updateBlock(idx, updates)}
                  onRemove={() => removeBlock(idx)}
                  onMove={(dir) => moveBlock(idx, dir)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Merge Tags Reference */}
      <div className="flex items-center gap-1 flex-wrap pt-1 border-t">
        <span className="text-[9px] text-muted-foreground font-medium mr-1">Merge tags:</span>
        {MERGE_TAGS.map(mt => (
          <button
            key={mt.tag}
            className="text-[9px] px-1.5 py-0.5 rounded bg-muted hover:bg-muted-foreground/10 text-muted-foreground font-mono transition-colors"
            onClick={() => navigator.clipboard.writeText(mt.tag).then(() => {})}
            title={`Click to copy: ${mt.tag}`}
            data-testid={`merge-tag-${mt.tag.replace(/[{}]/g, '')}`}
          >
            {mt.label}
          </button>
        ))}
      </div>
    </div>
  );
}


function BlockItem({ block, index, isSelected, isFirst, isLast, onSelect, onUpdate, onRemove, onMove }) {
  return (
    <div
      className={`group border rounded-lg transition-all ${isSelected ? 'border-primary ring-1 ring-primary/20' : 'border-border hover:border-muted-foreground/30'}`}
      data-testid={`block-item-${index}`}
    >
      {/* Block header */}
      <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 border-b cursor-pointer" onClick={onSelect}>
        <GripVertical className="w-3 h-3 text-muted-foreground/50" />
        <span className="text-[10px] font-medium text-muted-foreground uppercase flex-1">
          {block.type}
        </span>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={(e) => { e.stopPropagation(); onMove(-1); }} disabled={isFirst}>
            <ChevronUp className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={(e) => { e.stopPropagation(); onMove(1); }} disabled={isLast}>
            <ChevronDown className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Block preview / editor */}
      <div className="px-3 py-2">
        {isSelected ? (
          <BlockEditor block={block} onUpdate={onUpdate} />
        ) : (
          <BlockPreview block={block} />
        )}
      </div>
    </div>
  );
}


function BlockPreview({ block }) {
  switch (block.type) {
    case 'text':
      return <div className="text-xs text-muted-foreground truncate" dangerouslySetInnerHTML={{ __html: block.content?.slice(0, 120) || 'Empty text block' }} />;
    case 'image':
      return block.src ? (
        <img src={block.src} alt={block.alt} className="max-h-12 rounded" />
      ) : (
        <div className="flex items-center gap-1 text-xs text-muted-foreground"><Image className="w-3 h-3" /> No image set</div>
      );
    case 'button':
      return (
        <div className={`text-${block.align || 'center'}`}>
          <span className="inline-block px-3 py-1 rounded text-[10px] font-medium" style={{ backgroundColor: block.bgColor, color: block.textColor }}>
            {block.text || 'Button'}
          </span>
        </div>
      );
    case 'divider':
      return <hr style={{ borderColor: block.color, borderWidth: `${block.thickness}px` }} />;
    case 'spacer':
      return <div className="text-[10px] text-muted-foreground text-center">{block.height}px spacer</div>;
    default:
      return <div className="text-xs text-muted-foreground">Unknown block</div>;
  }
}


function BlockEditor({ block, onUpdate }) {
  switch (block.type) {
    case 'text':
      return <TextBlockEditor block={block} onUpdate={onUpdate} />;
    case 'image':
      return <ImageBlockEditor block={block} onUpdate={onUpdate} />;
    case 'button':
      return <ButtonBlockEditor block={block} onUpdate={onUpdate} />;
    case 'divider':
      return <DividerBlockEditor block={block} onUpdate={onUpdate} />;
    case 'spacer':
      return <SpacerBlockEditor block={block} onUpdate={onUpdate} />;
    default:
      return null;
  }
}


function TextBlockEditor({ block, onUpdate }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 mb-1">
        {['left', 'center', 'right'].map(a => (
          <Button
            key={a}
            variant={block.align === a ? 'default' : 'outline'}
            size="sm" className="h-6 w-6 p-0"
            onClick={() => onUpdate({ align: a })}
          >
            {a === 'left' ? <AlignLeft className="w-3 h-3" /> : a === 'center' ? <AlignCenter className="w-3 h-3" /> : <AlignRight className="w-3 h-3" />}
          </Button>
        ))}
      </div>
      <Textarea
        value={block.content}
        onChange={e => onUpdate({ content: e.target.value })}
        placeholder="Enter HTML or plain text..."
        rows={4}
        className="text-xs font-mono resize-y"
        data-testid="text-block-content"
      />
      <p className="text-[9px] text-muted-foreground">Supports HTML. Use merge tags like {'{{customer_name}}'}</p>
    </div>
  );
}


function ImageBlockEditor({ block, onUpdate }) {
  return (
    <div className="space-y-2">
      <div>
        <label className="text-[10px] font-medium text-muted-foreground">Image URL</label>
        <Input
          value={block.src}
          onChange={e => onUpdate({ src: e.target.value })}
          placeholder="https://example.com/image.jpg"
          className="h-7 text-xs mt-0.5"
          data-testid="image-block-src"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Alt Text</label>
          <Input value={block.alt} onChange={e => onUpdate({ alt: e.target.value })} className="h-7 text-xs mt-0.5" />
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Width</label>
          <Select value={block.width} onValueChange={v => onUpdate({ width: v })}>
            <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="100%">Full Width</SelectItem>
              <SelectItem value="75%">75%</SelectItem>
              <SelectItem value="50%">50%</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {block.src && <img src={block.src} alt={block.alt} className="max-h-24 rounded border mt-1" />}
    </div>
  );
}


function ButtonBlockEditor({ block, onUpdate }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Button Text</label>
          <Input value={block.text} onChange={e => onUpdate({ text: e.target.value })} className="h-7 text-xs mt-0.5" data-testid="button-block-text" />
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Link URL</label>
          <Input value={block.url} onChange={e => onUpdate({ url: e.target.value })} placeholder="https://..." className="h-7 text-xs mt-0.5" data-testid="button-block-url" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Background</label>
          <div className="flex items-center gap-1 mt-0.5">
            <input type="color" value={block.bgColor} onChange={e => onUpdate({ bgColor: e.target.value })} className="w-7 h-7 rounded cursor-pointer" />
            <Input value={block.bgColor} onChange={e => onUpdate({ bgColor: e.target.value })} className="h-7 text-xs flex-1" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Text Color</label>
          <div className="flex items-center gap-1 mt-0.5">
            <input type="color" value={block.textColor} onChange={e => onUpdate({ textColor: e.target.value })} className="w-7 h-7 rounded cursor-pointer" />
            <Input value={block.textColor} onChange={e => onUpdate({ textColor: e.target.value })} className="h-7 text-xs flex-1" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground">Radius (px)</label>
          <Input type="number" value={block.borderRadius} onChange={e => onUpdate({ borderRadius: e.target.value })} className="h-7 text-xs mt-0.5" />
        </div>
      </div>
      <div className="flex items-center gap-1">
        {['left', 'center', 'right'].map(a => (
          <Button key={a} variant={block.align === a ? 'default' : 'outline'} size="sm" className="h-6 w-6 p-0" onClick={() => onUpdate({ align: a })}>
            {a === 'left' ? <AlignLeft className="w-3 h-3" /> : a === 'center' ? <AlignCenter className="w-3 h-3" /> : <AlignRight className="w-3 h-3" />}
          </Button>
        ))}
      </div>
      <div className={`mt-1 text-${block.align || 'center'}`}>
        <span className="inline-block px-4 py-2 rounded text-xs font-medium" style={{ backgroundColor: block.bgColor, color: block.textColor, borderRadius: `${block.borderRadius}px` }}>
          {block.text || 'Button'}
        </span>
      </div>
    </div>
  );
}


function DividerBlockEditor({ block, onUpdate }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-[10px] font-medium text-muted-foreground">Color</label>
        <div className="flex items-center gap-1 mt-0.5">
          <input type="color" value={block.color} onChange={e => onUpdate({ color: e.target.value })} className="w-7 h-7 rounded cursor-pointer" />
          <Input value={block.color} onChange={e => onUpdate({ color: e.target.value })} className="h-7 text-xs flex-1" />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-medium text-muted-foreground">Thickness (px)</label>
        <Input type="number" value={block.thickness} onChange={e => onUpdate({ thickness: e.target.value })} className="h-7 text-xs mt-0.5" min={1} max={10} />
      </div>
    </div>
  );
}


function SpacerBlockEditor({ block, onUpdate }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-muted-foreground">Height (px)</label>
      <Input type="number" value={block.height} onChange={e => onUpdate({ height: e.target.value })} className="h-7 text-xs mt-0.5 w-24" min={5} max={100} />
    </div>
  );
}


/**
 * Convert blocks array to email-safe HTML
 */
export function blocksToHtml(blocks) {
  if (!blocks || blocks.length === 0) return '';

  const rows = blocks.map(block => {
    switch (block.type) {
      case 'text':
        return `<tr><td style="padding:8px 24px;text-align:${block.align || 'left'};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:15px;line-height:1.6;color:#374151;">${block.content || ''}</td></tr>`;

      case 'image':
        if (!block.src) return '';
        return `<tr><td style="padding:8px 24px;text-align:${block.align || 'center'};"><img src="${block.src}" alt="${block.alt || ''}" width="${block.width || '100%'}" style="max-width:${block.width || '100%'};height:auto;display:inline-block;border-radius:4px;" /></td></tr>`;

      case 'button':
        return `<tr><td style="padding:16px 24px;text-align:${block.align || 'center'};"><a href="${block.url || '#'}" target="_blank" style="display:inline-block;padding:12px 28px;background-color:${block.bgColor || '#2563eb'};color:${block.textColor || '#ffffff'};text-decoration:none;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;font-weight:600;border-radius:${block.borderRadius || '6'}px;mso-padding-alt:0;">${block.text || 'Click Here'}</a></td></tr>`;

      case 'divider':
        return `<tr><td style="padding:8px 24px;"><hr style="border:none;border-top:${block.thickness || '1'}px solid ${block.color || '#e5e7eb'};margin:0;" /></td></tr>`;

      case 'spacer':
        return `<tr><td style="height:${block.height || '20'}px;font-size:1px;line-height:1px;">&nbsp;</td></tr>`;

      default:
        return '';
    }
  }).filter(Boolean).join('\n');

  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;margin:0 auto;background-color:#ffffff;">
<tbody>
${rows}
</tbody>
</table>`;
}


/**
 * Parse HTML back into blocks (best effort)
 */
export function htmlToBlocks(html) {
  if (!html || !html.includes('<tr>')) return [];
  
  const blocks = [];
  const trRegex = /<tr>([\s\S]*?)<\/tr>/gi;
  let match;
  
  while ((match = trRegex.exec(html)) !== null) {
    const content = match[1].trim();
    
    if (content.includes('<img')) {
      const src = content.match(/src="([^"]*)"/) || [];
      const alt = content.match(/alt="([^"]*)"/) || [];
      const width = content.match(/width="([^"]*)"/) || [];
      const align = content.match(/text-align:\s*(left|center|right)/) || [];
      blocks.push({ type: 'image', id: Date.now().toString() + blocks.length, src: src[1] || '', alt: alt[1] || '', width: width[1] || '100%', align: align[1] || 'center' });
    } else if (content.includes('<a') && content.includes('display:inline-block')) {
      const text = content.match(/>([^<]*)<\/a>/) || [];
      const url = content.match(/href="([^"]*)"/) || [];
      const bg = content.match(/background-color:\s*([^;]*)/) || [];
      const color = content.match(/(?<!background-)color:\s*([^;]*)/) || [];
      const radius = content.match(/border-radius:\s*(\d+)/) || [];
      const align = content.match(/text-align:\s*(left|center|right)/) || [];
      blocks.push({ type: 'button', id: Date.now().toString() + blocks.length, text: text[1] || 'Button', url: url[1] || '', bgColor: bg[1] || '#2563eb', textColor: color[1] || '#ffffff', borderRadius: radius[1] || '6', align: align[1] || 'center' });
    } else if (content.includes('<hr')) {
      const color = content.match(/border-top:\s*\d+px\s+solid\s+([^;"]*)/) || [];
      const thickness = content.match(/border-top:\s*(\d+)px/) || [];
      blocks.push({ type: 'divider', id: Date.now().toString() + blocks.length, color: color[1] || '#e5e7eb', thickness: thickness[1] || '1' });
    } else if (content.includes('&nbsp;') && content.includes('height:')) {
      const height = content.match(/height:\s*(\d+)px/) || [];
      blocks.push({ type: 'spacer', id: Date.now().toString() + blocks.length, height: height[1] || '20' });
    } else if (content.includes('<td')) {
      const inner = content.match(/<td[^>]*>([\s\S]*)<\/td>/) || [];
      const align = content.match(/text-align:\s*(left|center|right)/) || [];
      if (inner[1] && inner[1].trim()) {
        blocks.push({ type: 'text', id: Date.now().toString() + blocks.length, content: inner[1].trim(), align: align[1] || 'left' });
      }
    }
  }
  
  return blocks;
}
